import {Inject, Injectable, InjectionToken} from "@angular/core";
import {AuthService} from "../auth/auth.service";
import {catchError, EMPTY, identity, mergeMap, Observable, throwError, timeout} from "rxjs";
import {VersionService} from "../versioning/version.service";
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse} from "@angular/common/http";

import {DataClientError} from "../error/data-client-error";

/**
 * JwtHttpClientService is a wrapper over Angular HttpClient. It manages the JWT auth header.
 */
@Injectable()
export class JwtHttpClientService {
    /**
     * Token for injected http timeout value
     * @type {InjectionToken}
     */
    public static readonly HTTP_TIMEOUT_KEY: InjectionToken<string> = new InjectionToken<string>('httpTimeout');

    constructor(private httpClient: HttpClient,
                private authService: AuthService,
                private versionService: VersionService,
                @Inject(JwtHttpClientService.HTTP_TIMEOUT_KEY) protected httpTimeout: number) {
    }

    get(url: string, params?: HttpParams, headers?: HttpHeaders, responseType: "json" | "blob" | "text" = "json", httpTimeout?: number): Observable<HttpResponse<Object | Blob | string>> {
        return this._request("GET", url, null, params, headers, responseType, httpTimeout);
    }

    getWithoutToken(url: string, params?: HttpParams, headers: HttpHeaders = new HttpHeaders(), httpTimeout: number = this.httpTimeout): Observable<HttpResponse<Object>> {
        return this.httpClient.request("GET", (this.authService.dataserverURL || "") + url, {
            headers: this.versionService.setVersionHeader(headers),
            params: params,
            observe: "response",
            responseType: "json"
        }).pipe(
            (httpTimeout ? timeout(httpTimeout) : identity),
            catchError((response: HttpErrorResponse) => {
                if (response && response.status === 406) {
                    this.versionService.versionMismatch$.next();
                }
                return throwError(() => new DataClientError(response, "json"));
            })
        );
    }

    post(url: string, body: any, params?: HttpParams, headers?: HttpHeaders, responseType: "json" | "blob" | "text" = "json", httpTimeout?: number): Observable<HttpResponse<Object | Blob | string>> {
        return this._request("POST", url, body, params, headers, responseType, httpTimeout);
    }

    put(url: string, body: any, params?: HttpParams, headers?: HttpHeaders, responseType: "json" | "blob" | "text" = "json", httpTimeout?: number): Observable<HttpResponse<Object | Blob | string>> {
        return this._request("PUT", url, body, params, headers, responseType, httpTimeout);
    }

    delete(url: string, params?: HttpParams, headers?: HttpHeaders, httpTimeout?: number): Observable<HttpResponse<string>> {
        return <Observable<HttpResponse<string>>> this._request("DELETE", url, null, params, headers, "text", httpTimeout);
    }

    public patch(url: string, body: string, params?: HttpParams, headers?: HttpHeaders, httpTimeout?: number): Observable<HttpResponse<Object>> {
        return this._request("PATCH", url, body, params, headers, undefined, httpTimeout);
    }

    public head(url: string, params?: HttpParams, headers?: HttpHeaders, httpTimeout?: number): Observable<HttpResponse<Object>> {
        return this._request("HEAD", url, null, params, headers, undefined, httpTimeout);
    }

    private _request(method: string, url: string, body?: any, params?: HttpParams, headers: HttpHeaders = new HttpHeaders(), responseType: "json" | "blob" | "text" = "json", httpTimeout?: number): Observable<HttpResponse<Object | Blob | string>> {
        headers = this.versionService.setVersionHeader(this.authService.setAuthorizationAndLocationHeaders(headers));
        if (this.authService.isAccessTokenExpired()) {
            return this.authService.refreshAccessToken().pipe(
                mergeMap((result: Boolean) => {
                    if (!result) {
                        return EMPTY;
                    }
                    return this.performRequest(method, url, body, params, this.authService.setAuthorizationAndLocationHeaders(headers), responseType, httpTimeout);
                })
            );
        }
        return this.performRequest(method, url, body, params, headers, responseType, httpTimeout);
    }

    private performRequest(method: string, url: string, body: any, params: HttpParams, headers: HttpHeaders, responseType: "json" | "blob" | "text", httpTimeout: number = this.httpTimeout): Observable<HttpResponse<Object | Blob | string>> {
        if (!url || (!url.startsWith("http:") && !url.startsWith("https:"))) {
            url = this.authService.dataserverURL + url;
        }
        return this.httpClient.request(method, url, {
            headers: headers,
            params: params,
            body: body,
            observe: "response",
            responseType: responseType
        }).pipe(
            (httpTimeout ? timeout(httpTimeout) : identity),
            catchError((response: HttpErrorResponse) => {
                if (response.status === 401) {

                    // Token might be expired, attempt refresh token and retry
                    return this.authService.refreshAccessToken().pipe(
                        mergeMap((refreshed: boolean) => {
                                if (!refreshed) {
                                    throw response;
                                }
                                return this.httpClient.request(method, url, {
                                    headers: this.authService.setAuthorizationAndLocationHeaders(headers),
                                    params: params,
                                    body: body,
                                    observe: "response",
                                    responseType: responseType
                                });
                            }
                        )
                    );
                }

                if (response.status === 406) {
                    this.versionService.versionMismatch$.next();
                }
                return throwError(() => new DataClientError(response, responseType));
            })
        );
    }
}
